import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieGrid from '@/components/feature/MovieGrid';
import SectionHeading from '@/components/feature/SectionHeading';
import { categoryMeta, getMovieById, getRelated } from '@/utils/movieUtils';
import { useFavorites } from '@/hooks/useFavorites';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = id ? getMovieById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  useDocumentMeta({
    title: movie ? `${movie.title} - 午夜视频影视详情` : '影视详情 - 午夜视频',
    description: movie
      ? `${movie.title}（${movie.year}）${movie.typeLabel}，${movie.genres.join('、')}题材，由${movie.director}执导。${movie.synopsis.slice(0, 60)}`
      : '午夜视频影视详情页，查看影片简介、主演与相关推荐。',
    keywords: movie ? `${movie.title},${movie.genres.join(',')},午夜视频,${movie.typeLabel}` : '午夜视频,影视详情',
  });

  if (!movie) {
    return (
      <div className="pt-16 md:pt-20 min-h-[70vh] flex flex-col items-center justify-center px-4 text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该影视内容</h1>
        <p className="mt-2 text-sm text-foreground-600">资源可能已被移除，去首页看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-11 px-6 items-center rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = categoryMeta[movie.type];
  const related = getRelated(movie, 5);
  const favorited = isFavorite(movie.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be unavailable */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="pt-16 md:pt-20">
      <section className="relative overflow-hidden border-b border-background-200">
        <img
          src={movie.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top blur-2xl scale-110 opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/90 to-background-950" />
        <div className="relative px-4 md:px-8 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: category.label, to: `/category/${movie.type}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="w-[200px] md:w-[280px] shrink-0">
              <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300 bg-background-300">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - 午夜视频`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-950 text-xs font-bold">
                  {movie.typeLabel}
                </span>
                <span className="flex items-center gap-1 text-primary-400 font-bold">
                  <i className="ri-star-fill"></i>
                  {movie.rating.toFixed(1)}
                </span>
                <span className="text-sm text-foreground-500">{movie.year}</span>
                <span className="text-background-400">·</span>
                <span className="text-sm text-foreground-500">{movie.region}</span>
                <span className="text-background-400">·</span>
                <span className="text-sm text-foreground-500">{movie.duration}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <span key={g} className="px-3 py-1 rounded-full bg-secondary-100/10 text-secondary-300 text-xs">
                    {g}
                  </span>
                ))}
              </div>

              <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600 max-w-2xl">{movie.synopsis}</p>

              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl text-sm">
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">导演</dt>
                  <dd className="text-foreground-800">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 shrink-0">类型</dt>
                  <dd className="text-foreground-800">{movie.genres.join(' / ')}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-foreground-500 shrink-0">主演</dt>
                  <dd className="text-foreground-800">{movie.cast.join('、')}</dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    favorited
                      ? 'bg-accent-500 text-background-950'
                      : 'border border-background-300 text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill text-lg' : 'ri-heart-line text-lg'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-900 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200 transition-colors"
                >
                  <i className={copied ? 'ri-check-line text-lg text-primary-400' : 'ri-share-line text-lg'}></i>
                  {copied ? '链接已复制' : '分享链接'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/category/${movie.type}`)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md text-primary-400 text-sm font-semibold whitespace-nowrap cursor-pointer hover:text-primary-300 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回{movie.typeLabel}列表
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10 md:py-14">
        <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl font-bold text-foreground-950">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
            <i className="ri-file-text-line"></i>
          </span>
          内容简介
        </h2>
        <p className="mt-4 max-w-3xl text-sm md:text-base leading-loose text-foreground-600">{movie.synopsis}</p>
        <p className="mt-4 max-w-3xl text-sm md:text-base leading-loose text-foreground-600">
          《{movie.title}》是 {movie.year} 年上映的{movie.region}{movie.typeLabel}，由 {movie.director} 执导，
          {movie.cast.join('、')} 主演，属于{movie.genres.join('、')}题材。本站内容为演示数据，仅用于展示影视资讯与分类浏览体验。
        </p>
      </section>

      {related.length > 0 && (
        <section className="px-4 md:px-8 pb-8">
          <SectionHeading
            icon="ri-magic-line"
            title="相关推荐"
            subtitle={`喜欢《${movie.title}》的话，这些作品也许同样适合你`}
            moreTo={`/category/${movie.type}`}
            moreLabel={`更多${movie.typeLabel}`}
          />
          <MovieGrid items={related} />
        </section>
      )}
    </div>
  );
}